import { PublishedFamilyTreeRecord } from '../types/familyTree';
import { LabResultRecord } from '../types/labs';
import { PersonalizedMelioIntelligenceItem } from '../types/melioIntelligence';
import { MyInputsCurrentProfile } from '../types/myInputs';
import { SurveillancePlanItem, SurveillancePlanRecord, SurveillanceReviewPrompt } from '../types/surveillance';
import { buildMyInputsOverview } from '../services/myInputsService';
import { coerceDate, formatDateLabel, formatDueLabel } from './dateLabels';

export interface MemberProfileSnapshot {
  firstName?: string;
  lastName?: string;
  displayName?: string;
  familyHistorySummary?: string;
  maternalHistory?: string;
  paternalHistory?: string;
  siblingsHistory?: string;
  childrenHistory?: string;
  healthGoals?: string;
  membership?: {
    status?: string;
    accessType?: string;
    packageId?: string;
    packageName?: string;
    includedDays?: number;
    startedAt?: unknown;
    endsAt?: unknown;
  };
}

export interface MemberEngagementContext {
  profile: MemberProfileSnapshot | null;
  myInputsProfile?: MyInputsCurrentProfile | null;
  medicalDocCount: number;
  wearableDocCount: number;
  labResults: LabResultRecord[];
  latestPlan: SurveillancePlanRecord | null;
  openPrompts: SurveillanceReviewPrompt[];
  familyTree: PublishedFamilyTreeRecord | null;
  intelligenceFeaturedItem?: PersonalizedMelioIntelligenceItem | null;
}

export interface MembershipPillar {
  id: string;
  title: string;
  value: string;
  detail: string;
}

export interface LinkAction {
  label: string;
  path: string;
}

export interface TodayAtMelio {
  eyebrow: string;
  title: string;
  detail: string;
  membershipNote: string;
  primaryAction: LinkAction;
  quickActions: LinkAction[];
  watchTitle: string;
  watchDetail: string;
}

export interface SignalCard {
  id: string;
  eyebrow: string;
  title: string;
  detail: string;
  meta: string;
  path: string;
  ctaLabel: string;
}

export interface OpenLoopCard {
  id: string;
  title: string;
  detail: string;
  urgency: string;
  path: string;
  ctaLabel: string;
}

export interface BriefSection {
  title: string;
  summary: string;
  bullets: string[];
}

export interface FamilyIntelligencePromptCard {
  id: string;
  title: string;
  detail: string;
  path: string;
  ctaLabel: string;
}

export interface DueActionCard {
  id: string;
  title: string;
  detail: string;
  status: string;
  nextDueLabel: string;
}

export interface MyInputsCard {
  title: string;
  subtext: string;
  status: string;
  ctaLabel: string;
  path: string;
  prompt?: string;
}

export interface IntelligenceTeaserCard {
  title: string;
  summary: string;
  explanation?: string | null;
  ctaLabel: string;
  path: string;
}

export interface PlanActivityEntry {
  id: string;
  title: string;
  detail: string;
  dateLabel: string;
}

// Date coercion and phrasing now live in `utils/dateLabels.ts` so the dashboard
// utils can share them without pulling in this whole module. Re-exported here
// because three routed pages already import them from this path.
export { coerceDate, formatDateLabel };

export function getLatestLabResult(results: LabResultRecord[]) {
  return [...results].sort((a, b) => {
    const aTime = coerceDate(a.observedAt || a.updatedAt || a.createdAt)?.getTime() ?? 0;
    const bTime = coerceDate(b.observedAt || b.updatedAt || b.createdAt)?.getTime() ?? 0;
    return bTime - aTime;
  })[0] || null;
}

function getNextDueItem(plan: SurveillancePlanRecord | null): SurveillancePlanItem | null {
  if (!plan?.items?.length) return null;

  const datedItems = plan.items
    .filter((item) => coerceDate(item.nextDue))
    .sort((a, b) => (coerceDate(a.nextDue)?.getTime() ?? Number.MAX_SAFE_INTEGER) - (coerceDate(b.nextDue)?.getTime() ?? Number.MAX_SAFE_INTEGER));

  if (datedItems.length > 0) return datedItems[0];
  return plan.items[0];
}

export function buildMembershipPillars(context: MemberEngagementContext): MembershipPillar[] {
  const nextDue = getNextDueItem(context.latestPlan);
  const latestLab = getLatestLabResult(context.labResults);
  const connectedSources = context.medicalDocCount + context.wearableDocCount + context.labResults.length;
  const activeMembershipEnd = context.profile?.membership?.status === 'active'
    ? formatDateLabel(context.profile.membership.endsAt)
    : null;

  return [
    {
      id: 'interpretation',
      title: 'Interpretation',
      value: activeMembershipEnd ? `Active through ${activeMembershipEnd}` : (context.latestPlan ? `Version ${context.latestPlan.version}` : 'Preparing first review'),
      detail: latestLab
        ? `Latest signal: ${latestLab.biomarker} reviewed against your plan and held in the ongoing record.`
        : 'New reports and biomarker changes can be folded into the next clinician-reviewed plan refresh.',
    },
    {
      id: 'continuity',
      title: 'Continuity',
      value: connectedSources > 0 ? `${connectedSources} sources linked` : 'Building your record',
      detail: activeMembershipEnd
        ? `Your membership is currently live${context.profile?.membership?.accessType === 'included' ? ` through ${activeMembershipEnd}` : ''}, with continuity across uploads, family context, and follow-up.`
        : context.familyTree?.status === 'published'
          ? 'Your family history, uploads, and plan now live in one prevention record.'
          : 'Melio is gathering genetics, history, and uploads into a single longitudinal record.',
    },
    {
      id: 'guidance',
      title: 'Guidance',
      value: nextDue ? formatDueLabel(nextDue.nextDue) : 'Next step being defined',
      detail: nextDue
        ? `${nextDue.modality}: ${nextDue.action}. This is the visible next item in your access and surveillance schedule.`
        : 'Once your first review is complete, Melio will sequence what matters now, what can wait, and what should be coordinated next.',
    },
    {
      id: 'progress',
      title: 'Progress',
      value: context.openPrompts.length > 0 ? `${context.openPrompts.length} open loop${context.openPrompts.length === 1 ? '' : 's'}` : 'On track',
      detail: context.familyTree?.status === 'published'
        ? 'Your inherited context is unlocked and can keep sharpening the schedule over time.'
        : 'Every answered prompt, upload, and check-in makes the prevention and navigation layer more precise.',
    },
  ];
}

export function buildMyInputsCard(context: MemberEngagementContext): MyInputsCard {
  const overview = buildMyInputsOverview(context.myInputsProfile || null);
  const hasAnyInputs = overview.currentMedicationCount > 0 || overview.currentSupplementCount > 0 || overview.dietSummary !== 'No diet profile yet';

  return {
    title: 'My Inputs',
    subtext: `${overview.currentMedicationCount} medication${overview.currentMedicationCount === 1 ? '' : 's'} • ${overview.currentSupplementCount} supplement${overview.currentSupplementCount === 1 ? '' : 's'} • ${overview.dietSummary}`,
    status: overview.updatedLabel,
    ctaLabel: 'Review inputs',
    path: '/history',
    prompt: hasAnyInputs ? undefined : 'Your current inputs can improve plan relevance and intelligence personalization.',
  };
}

export function buildIntelligenceTeaser(context: MemberEngagementContext): IntelligenceTeaserCard | null {
  if (!context.intelligenceFeaturedItem) return null;

  return {
    title: context.intelligenceFeaturedItem.title,
    summary: context.intelligenceFeaturedItem.melioSummary,
    explanation: context.intelligenceFeaturedItem.personalizationReason,
    ctaLabel: 'Open Melio Intelligence',
    path: '/intelligence',
  };
}

export function buildTodayAtMelio(context: MemberEngagementContext): TodayAtMelio {
  const nextDue = getNextDueItem(context.latestPlan);
  const latestLab = getLatestLabResult(context.labResults);
  const firstPrompt = context.openPrompts[0];
  const familyTreeUnlocked = context.familyTree?.status === 'published';

  if (firstPrompt) {
    return {
      eyebrow: 'Today at Melio',
      title: firstPrompt.title,
      detail: firstPrompt.detail,
      membershipNote: 'Use Melio to close the open clinical items that still need attention.',
      primaryAction: { label: 'Review open items', path: '/results' },
      quickActions: [
        { label: 'Upload supporting data', path: '/intelligence?tab=uploads' },
        { label: 'Refresh health history', path: '/history' },
        { label: 'Book follow-up consult', path: '/enrollment' },
      ],
      watchTitle: 'Why open the app now',
      watchDetail: `${context.openPrompts.length} item${context.openPrompts.length === 1 ? '' : 's'} is waiting for closure before the next plan update.`,
    };
  }

  if (!familyTreeUnlocked) {
    return {
      eyebrow: 'Today at Melio',
      title: 'Complete your family review.',
      detail: 'Your clinician still needs to structure the inherited picture behind your plan before this part of the record is unlocked.',
      membershipNote: 'Once reviewed, your family history becomes part of the record that supports future planning.',
      primaryAction: { label: 'Book family review', path: '/book/clinical-history' },
      quickActions: [
        { label: 'Update family history', path: '/history' },
        { label: 'Upload prior reports', path: '/intelligence?tab=uploads' },
        { label: 'Review current plan', path: '/results' },
      ],
      watchTitle: 'What happens next',
      watchDetail: 'After review, the structured family history can be used in the plan and shown back to you as a clinician-reviewed record.',
    };
  }

  if (!context.latestPlan) {
    return {
      eyebrow: 'Today at Melio',
      title: 'Set your prevention baseline.',
      detail: 'The next meaningful move is to give Melio enough context to draft the first clinician-reviewed plan.',
      membershipNote: 'The assessment period is about building the record that supports longer-term continuity.',
      primaryAction: { label: 'Upload your first data', path: '/intelligence?tab=uploads' },
      quickActions: [
        { label: 'Update health history', path: '/history' },
        { label: 'Book family review', path: '/book/clinical-history' },
        { label: 'View history', path: '/history' },
      ],
      watchTitle: 'What happens next',
      watchDetail: 'Once your baseline context is in place, Melio can move from intake into a clinician-reviewed plan.',
    };
  }

  if (nextDue) {
    return {
      eyebrow: 'Today at Melio',
      title: `${nextDue.modality}: ${nextDue.action}`,
      detail: nextDue.reason,
      membershipNote: 'This is the next item currently due in your plan.',
      primaryAction: { label: 'Open your plan', path: '/results' },
      quickActions: [
        { label: 'Upload new results', path: '/intelligence?tab=uploads' },
        { label: 'Refine your history', path: '/history' },
        { label: 'Book follow-up consult', path: '/enrollment' },
      ],
      watchTitle: 'Timing',
      watchDetail: formatDueLabel(nextDue.nextDue),
    };
  }

  return {
    eyebrow: 'Today at Melio',
    title: 'Your plan is current.',
    detail: latestLab
      ? `${latestLab.biomarker} is the latest signal on file. Keep your record fresh as new data arrives.`
      : 'There is no urgent action right now. Melio is holding the context so the next signal can be interpreted quickly.',
    membershipNote: 'Quiet months still matter: Melio is maintaining your record, schedule, and readiness for the next meaningful change.',
    primaryAction: { label: 'Review your plan', path: '/results' },
    quickActions: [
      { label: 'Upload new data', path: '/intelligence?tab=uploads' },
      { label: 'View history', path: '/history' },
      { label: 'Set health goals', path: '/history' },
    ],
    watchTitle: 'Quarterly continuity',
    watchDetail: 'Even when the plan is stable, the service should feel ready the moment something changes or something becomes due.',
  };
}

export function buildSignalCards(context: MemberEngagementContext): SignalCard[] {
  const cards: SignalCard[] = [];
  const latestLab = getLatestLabResult(context.labResults);

  if (context.latestPlan) {
    cards.push({
      id: 'plan',
      eyebrow: 'Plan signal',
      title: `Version ${context.latestPlan.version} is live`,
      detail: context.latestPlan.patientSummary || context.latestPlan.summary,
      meta: `Updated ${formatDateLabel(context.latestPlan.updatedAt)}`,
      path: '/results',
      ctaLabel: 'Open plan',
    });
  }

  if (latestLab) {
    cards.push({
      id: 'lab',
      eyebrow: 'Latest biomarker',
      title: `${latestLab.biomarker}: ${latestLab.value}${latestLab.unit ? ` ${latestLab.unit}` : ''}`,
      detail: latestLab.sourceDocumentName
        ? `Entered from ${latestLab.sourceDocumentName}.`
        : 'New biomarker data is ready to be interpreted against your current plan.',
      meta: formatDateLabel(latestLab.observedAt || latestLab.updatedAt || latestLab.createdAt),
      path: '/intelligence?tab=uploads',
      ctaLabel: 'View uploads',
    });
  }

  if (context.openPrompts.length > 0) {
    cards.push({
      id: 'prompt',
      eyebrow: 'Awaiting review',
      title: `${context.openPrompts.length} open clinical loop${context.openPrompts.length === 1 ? '' : 's'}`,
      detail: context.openPrompts[0].detail,
      meta: context.openPrompts[0].title,
      path: '/results',
      ctaLabel: 'Resolve now',
    });
  }

  if (context.familyTree?.status === 'published') {
    cards.push({
      id: 'family-tree',
      eyebrow: 'Family context',
      title: 'Your family tree is now informing the plan',
      detail: context.familyTree.summaryNarrative,
      meta: context.familyTree.memberMessage || 'Clinician reviewed',
      path: '/history',
      ctaLabel: 'Open history',
    });
  } else {
    cards.push({
      id: 'family-tree-locked',
      eyebrow: 'Family context',
      title: 'Your inherited map is still waiting to be unlocked',
      detail: 'A structured family review is one of the most leverage-rich things you can do inside Melio.',
      meta: 'Clinician review required',
      path: '/book/clinical-history',
      ctaLabel: 'Book review',
    });
  }

  if (context.wearableDocCount > 0) {
    cards.push({
      id: 'wearables',
      eyebrow: 'Passive data',
      title: `${context.wearableDocCount} wearable export${context.wearableDocCount === 1 ? '' : 's'} on file`,
      detail: 'Wearable data is most useful when it changes the schedule, opens a loop, or sharpens what follow-up should happen next.',
      meta: 'Longitudinal context',
      path: '/intelligence?tab=uploads',
      ctaLabel: 'Manage data',
    });
  }

  return cards.slice(0, 4);
}

export function buildOpenLoopCards(context: MemberEngagementContext): OpenLoopCard[] {
  const items: OpenLoopCard[] = context.openPrompts.slice(0, 3).map((prompt) => ({
    id: prompt.id,
    title: prompt.title,
    detail: prompt.detail,
    urgency: 'Awaiting review',
    path: '/results',
    ctaLabel: 'Review prompt',
  }));

  if (!context.profile?.familyHistorySummary) {
    items.push({
      id: 'family-summary',
      title: 'Add a short family-history summary',
      detail: 'Even a concise summary helps Melio frame new results against the right inherited context.',
      urgency: 'Important',
      path: '/history',
      ctaLabel: 'Update history',
    });
  }

  if (context.familyTree?.status !== 'published') {
    items.push({
      id: 'family-review',
      title: 'Complete your clinician-led family review',
      detail: 'This unlocks the family tree and improves how targeted the plan can be.',
      urgency: 'High leverage',
      path: '/book/clinical-history',
      ctaLabel: 'Book review',
    });
  }

  if (context.medicalDocCount === 0 && context.labResults.length === 0) {
    items.push({
      id: 'first-upload',
      title: 'Upload your first report or biomarker set',
      detail: 'Melio only feels live when there is fresh data to interpret.',
      urgency: 'Foundational',
      path: '/intelligence?tab=uploads',
      ctaLabel: 'Upload now',
    });
  }

  if (!context.profile?.healthGoals) {
    items.push({
      id: 'health-goals',
      title: 'Set your health goals',
      detail: 'This gives future summaries a better sense of what outcomes matter most to you.',
      urgency: 'Helpful',
      path: '/history',
      ctaLabel: 'Add goals',
    });
  }

  return items.slice(0, 5);
}

export function buildMelioBrief(context: MemberEngagementContext): BriefSection {
  const nextDue = getNextDueItem(context.latestPlan);
  const latestLab = getLatestLabResult(context.labResults);
  const bullets: string[] = [];

  if (context.latestPlan) {
    bullets.push(`Current plan version: ${context.latestPlan.version}, last updated ${formatDateLabel(context.latestPlan.updatedAt)}.`);
  } else {
    bullets.push('Your first clinician-reviewed surveillance plan is still being assembled.');
  }

  if (nextDue) {
    bullets.push(`${nextDue.modality} is the next item on deck. ${formatDueLabel(nextDue.nextDue)}.`);
  } else {
    bullets.push('There is no urgent due item yet, which is a good time to tighten context and uploads.');
  }

  if (latestLab) {
    bullets.push(`Latest biomarker on file: ${latestLab.biomarker} recorded ${formatDateLabel(latestLab.observedAt || latestLab.updatedAt || latestLab.createdAt)}.`);
  } else {
    bullets.push('No structured biomarker signal has been added yet.');
  }

  if (context.familyTree?.status === 'published') {
    bullets.push('Your family intelligence layer is unlocked and now helps explain plan choices.');
  } else {
    bullets.push('The family intelligence layer is still waiting on clinician review.');
  }

  return {
    title: "This week's Melio Brief",
    summary: context.latestPlan
      ? 'Melio is most valuable when the next step, the reason behind it, and the context around it stay easy to understand.'
      : 'You are still in setup mode. The immediate job is to gather enough context for a high-quality first plan.',
    bullets: bullets.slice(0, 3),
  };
}

export function buildFamilyIntelligencePrompts(context: MemberEngagementContext): FamilyIntelligencePromptCard[] {
  const prompts: FamilyIntelligencePromptCard[] = [];
  const riskCategories = context.latestPlan?.geneticContext?.riskCategories || [];

  if (!context.profile?.maternalHistory) {
    prompts.push({
      id: 'maternal-history',
      title: 'Fill in maternal family detail',
      detail: 'Maternal history often changes how cancer and metabolic patterns are interpreted over time.',
      path: '/history',
      ctaLabel: 'Add maternal history',
    });
  }

  if (!context.profile?.paternalHistory) {
    prompts.push({
      id: 'paternal-history',
      title: 'Fill in paternal family detail',
      detail: 'Paternal patterns are easy to under-capture and can materially change screening cadence.',
      path: '/history',
      ctaLabel: 'Add paternal history',
    });
  }

  if (!context.profile?.siblingsHistory) {
    prompts.push({
      id: 'siblings-history',
      title: 'Add sibling context',
      detail: 'Sibling history can strengthen or soften the relevance of inherited risk patterns.',
      path: '/history',
      ctaLabel: 'Add sibling history',
    });
  }

  if (riskCategories.length > 0) {
    prompts.push({
      id: 'risk-category-follow-up',
      title: `Pressure-test ${riskCategories[0]} family context`,
      detail: 'If anyone in your family had related diagnoses, ages of onset can meaningfully change how Melio interprets the current plan.',
      path: '/history',
      ctaLabel: 'Refine details',
    });
  }

  if (context.familyTree?.status !== 'published') {
    prompts.push({
      id: 'publish-family-tree',
      title: 'Unlock the clinician-reviewed family tree',
      detail: 'This gives you a visual inherited map rather than scattered notes.',
      path: '/book/clinical-history',
      ctaLabel: 'Book family review',
    });
  }

  return prompts.slice(0, 3);
}

export function buildDueActionCards(plan: SurveillancePlanRecord | null): DueActionCard[] {
  if (!plan?.items?.length) return [];

  const withDates = [...plan.items].sort((a, b) => {
    const aTime = coerceDate(a.nextDue)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const bTime = coerceDate(b.nextDue)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

  return withDates.slice(0, 4).map((item, index) => ({
    id: `${item.action}-${index}`,
    title: `${item.modality}: ${item.action}`,
    detail: item.reason,
    status: formatDueLabel(item.nextDue),
    nextDueLabel: item.frequency,
  }));
}

export function buildPlanActivity(context: MemberEngagementContext): PlanActivityEntry[] {
  const entries: Array<PlanActivityEntry & { sortTime: number }> = [];
  const { latestPlan, familyTree, openPrompts } = context;

  if (latestPlan?.createdAt) {
    const date = coerceDate(latestPlan.createdAt);
    entries.push({
      id: 'plan-created',
      title: 'Prevention plan opened',
      detail: 'Your first plan record was created and is now the anchor for future updates.',
      dateLabel: formatDateLabel(latestPlan.createdAt),
      sortTime: date?.getTime() ?? 0,
    });
  }

  if (latestPlan?.signedOffAt) {
    const date = coerceDate(latestPlan.signedOffAt);
    entries.push({
      id: 'plan-signed',
      title: 'Clinician sign-off completed',
      detail: latestPlan.clinicianName
        ? `Signed off by ${latestPlan.clinicianName}.`
        : 'Your latest plan version has passed clinician review.',
      dateLabel: formatDateLabel(latestPlan.signedOffAt),
      sortTime: date?.getTime() ?? 0,
    });
  }

  if (latestPlan?.updatedAt) {
    const date = coerceDate(latestPlan.updatedAt);
    entries.push({
      id: 'plan-updated',
      title: `Plan version ${latestPlan.version} updated`,
      detail: 'This is the version currently guiding your cadence, reminders, and next-step logic.',
      dateLabel: formatDateLabel(latestPlan.updatedAt),
      sortTime: date?.getTime() ?? 0,
    });
  }

  if (familyTree?.status === 'published' && familyTree.publishedAt) {
    const date = coerceDate(familyTree.publishedAt);
    entries.push({
      id: 'family-tree-published',
      title: 'Family tree published',
      detail: 'Your inherited history is now available as a clinician-reviewed visual artifact.',
      dateLabel: formatDateLabel(familyTree.publishedAt),
      sortTime: date?.getTime() ?? 0,
    });
  }

  openPrompts.slice(0, 3).forEach((prompt, index) => {
    const date = coerceDate(prompt.createdAt);
    entries.push({
      id: `prompt-${prompt.id}`,
      title: prompt.title,
      detail: prompt.detail,
      dateLabel: prompt.createdAt ? formatDateLabel(prompt.createdAt) : `Open loop ${index + 1}`,
      sortTime: date?.getTime() ?? 0,
    });
  });

  return entries
    .sort((a, b) => b.sortTime - a.sortTime)
    .slice(0, 6)
    .map(({ sortTime: _sortTime, ...entry }) => entry);
}
